import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container, Box, Typography, Card, CardContent, Chip, Divider,
  CircularProgress, Alert
} from '@mui/material';
import {
  LocalShipping as TruckIcon, CheckCircle as CheckIcon,
  Schedule as ClockIcon, Restaurant as FoodIcon,
  ErrorOutline as ErrorIcon
} from '@mui/icons-material';
import config from '../config';

const REFRESH_INTERVAL = 15000;

const ESTADO_CONFIG = {
  EN_PREPARACION: { label: 'En preparación', color: '#F59E0B', icon: <FoodIcon /> },
  EN_CAMINO: { label: 'En camino hacia ti', color: '#3B82F6', icon: <TruckIcon /> },
  ENTREGADO: { label: 'Entregado', color: '#10B981', icon: <CheckIcon /> },
};

export default function SeguimientoEntrega() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${config.API_BASE_URL}/api/v1/delivery/destino/${token}`);
      const json = await res.json();
      if (!res.ok || !json.success) {
        setError(json.mensaje || 'Enlace no válido');
      } else {
        setData(json.data);
        setError(null);
      }
    } catch {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchData();
      intervalRef.current = setInterval(fetchData, REFRESH_INTERVAL);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [token, fetchData]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F8FAFC' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F8FAFC' }}>
        <Card sx={{ maxWidth: 400, textAlign: 'center', borderRadius: 3, p: 4 }}>
          <ErrorIcon sx={{ fontSize: 60, color: '#EF4444', mb: 2 }} />
          <Typography variant="h5" fontWeight={700} color="#EF4444" gutterBottom>No disponible</Typography>
          <Typography color="text.secondary">{error}</Typography>
        </Card>
      </Box>
    );
  }

  const { destino, orden, estado, repartidor, destino_coords } = data;
  const estadoInfo = ESTADO_CONFIG[estado] || ESTADO_CONFIG.EN_PREPARACION;

  // URL del mapa con repartidor y destino
  const mapUrl = repartidor && destino_coords?.lat
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${Math.min(repartidor.lng, destino_coords.lng) - 0.005},${Math.min(repartidor.lat, destino_coords.lat) - 0.003},${Math.max(repartidor.lng, destino_coords.lng) + 0.005},${Math.max(repartidor.lat, destino_coords.lat) + 0.003}&layer=mapnik&marker=${destino_coords.lat},${destino_coords.lng}`
    : null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1E2783', color: '#fff', py: 2.5, textAlign: 'center' }}>
        <Typography variant="h6" fontWeight={800}>{orden?.nombre_empresa || 'TRACKEA'}</Typography>
        <Typography variant="caption" sx={{ opacity: 0.8 }}>Seguimiento de entrega</Typography>
      </Box>

      <Container maxWidth="sm" sx={{ py: 3 }}>

        {/* Estado principal */}
        <Card sx={{ borderRadius: 3, mb: 2, border: `2px solid ${estadoInfo.color}20`, overflow: 'hidden' }}>
          <Box sx={{ bgcolor: `${estadoInfo.color}10`, p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ color: estadoInfo.color }}>{estadoInfo.icon}</Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight={700} color={estadoInfo.color}>
                {estadoInfo.label}
              </Typography>
              {estado === 'EN_CAMINO' && repartidor && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10B981', animation: 'pulse 1.5s infinite' }} />
                  <Typography variant="caption" color="#10B981" fontWeight={600}>
                    Repartidor en movimiento
                  </Typography>
                </Box>
              )}
            </Box>
            {destino?.entregado && destino?.fecha_entrega && (
              <Typography variant="caption" color="text.secondary">
                {new Date(destino.fecha_entrega).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </Typography>
            )}
          </Box>

          {/* Mapa del repartidor */}
          {mapUrl && estado === 'EN_CAMINO' && (
            <Box>
              <iframe
                src={mapUrl}
                style={{ width: '100%', height: 200, border: 'none' }}
                title="Ubicación del repartidor"
                loading="lazy"
              />
            </Box>
          )}
        </Card>

        {/* Detalle del pedido */}
        {destino?.detalle_entrega && (
          <Card sx={{ borderRadius: 3, mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} color="#1E2783" gutterBottom>
                Tu pedido
              </Typography>
              <Box sx={{ bgcolor: '#F8FAFC', borderRadius: 2, p: 2, border: '1px solid #E2E8F0' }}>
                <Typography variant="body2">{destino.detalle_entrega}</Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Dirección de entrega */}
        <Card sx={{ borderRadius: 3, mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" fontWeight={700} color="#1E2783" gutterBottom>
              Dirección de entrega
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {destino?.direccion || 'No especificada'}
            </Typography>
            {destino?.nombre_cliente && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {destino.nombre_cliente}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Foto de entrega */}
        {destino?.entregado && destino?.foto_url && (
          <Card sx={{ borderRadius: 3, mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} color="#10B981" gutterBottom>
                Evidencia de entrega
              </Typography>
              <Box
                component="img"
                src={`${config.API_BASE_URL}${destino.foto_url}`}
                sx={{ width: '100%', borderRadius: 2, maxHeight: 200, objectFit: 'cover' }}
              />
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <Typography variant="caption" color="text.disabled" textAlign="center" display="block" sx={{ mt: 3 }}>
          Powered by <a href="https://trackea.lat" style={{ color: '#1E2783', textDecoration: 'none', fontWeight: 600 }}>TRACKEA</a>
        </Typography>
      </Container>

      <style>{`
        @keyframes pulse {
          0% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.5); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </Box>
  );
}
